import { FORECAST_REFERENCE_QUERY_OPTIONS } from '~/lib/forecast-reference-queries'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import {
  type DashboardCardLoadState,
  resolveDashboardCardState,
  retryFailedDashboardQueries,
} from '~/components/dashboards/dashboard-card-state'
import { RotationLaunchNotesProvider } from '~/components/routes/product-rotations/rotation-launch-notes'
import { Button } from '~/components/ui/button'
import { InfoTooltip } from '~/components/ui/info-tooltip'
import { api } from '~/lib/tuyau'
import { usePermissionGuard, withRoutePermissions } from '~/utils/rbac'
import { BoutiqueChartModal, type BoutiqueScope } from './-boutique-chart-modal'
import { PrevisionsExportButton } from './-export-xlsx-button'
import type { PrevisionsExportInputs } from './-previsions-export-data'
import { useProductBucketsLoader } from './-previsions-product-buckets'
import { PREVISIONS_QUERY_OPTIONS } from './-previsions-query'
import { BoutiquesCard, SALES_BOUTIQUE_COLUMNS } from './-boutiques-card'
import { CategoriesTable } from './-categories-table'
import { FiltersBar } from './-filters-bar'
import {
  type AxisTotalsByGroupBy,
  buildCategoryTree,
  buildCategoryTreeFromTotals,
  buildProjectRows,
  buildStoreRows,
  buildTotals,
  buildTotalsFromAxis,
  type ForecastCategory,
} from './-previsions-data'
import {
  dayAndMonth,
  FORECAST_METHOD_LINES,
  FORECAST_MONTH_METHOD_LINES,
  FORECAST_WEEK_METHOD_LINES,
  salesChartTitle,
} from './-previsions-display'
import {
  type BucketProvenance,
  bucketRange,
  bucketStartOf,
  type Granularity,
  monthLongLabel,
  monthsOfRange,
  provenanceByMonthOf,
  provenanceByWeekOf,
  rangeForGranularity,
  resolveGranularity,
  type Series,
  seriesFromDays,
  seriesFromMonths,
  seriesFromWeeks,
  weekIsoLabel,
  weeksOfRange,
} from './-previsions-granularity'
import {
  EmptyFocusNotice,
  PrevisionsGrid,
  PrevisionsLoadingGrid,
  PrevisionsPageShell,
  ScopeBlockerNotice,
  sharedFiltersProps,
  usePrevisionsPage,
  useSetFocusDate,
} from './-previsions-page'
import { PrevisionsCardBoundary } from './-previsions-skeleton'
import { ProductionChart } from './-production-chart'
import { ReapproProjectionView } from './-reappro-projection-view'
import {
  effectiveCapping,
  isDateInRange,
  MEASURES,
  PREVISIONS_EXPORT_GROUP_BYS,
  previsionsSearchSchema,
  resolvePrevisionsRange,
  SEGMENT_CATEGORY_SOURCE,
  SEGMENT_GROUP_BY,
  type CategorySource,
  type Segment,
} from './-search-schema'
import { usePrevisionsView } from './-view-toggle'
import { TotauxCard } from './-totaux-card'

export const Route = createFileRoute('/_authenticated/previsions/')({
  validateSearch: previsionsSearchSchema,
  staticData: { permission: ['read', 'previsions.previsions.forecast'] },
  beforeLoad: ({ search }) => {
    if (!search.focusDate) return
    if (isDateInRange(search.focusDate, resolvePrevisionsRange(search))) return
    throw redirect({
      to: '/previsions',
      replace: true,
      search: { ...search, focusDate: undefined },
    })
  },
  component: PrevisionsPage,
})

withRoutePermissions(Route)

const NO_WEEKS: string[] = []
const NO_MONTHS: string[] = []
const METHOD_LINES: Record<Granularity, readonly string[]> = {
  day: FORECAST_METHOD_LINES,
  week: FORECAST_WEEK_METHOD_LINES,
  month: FORECAST_MONTH_METHOD_LINES,
}

const bucketsOf = (
  mode: Granularity | 'pending',
  weeks: string[],
  months: string[],
): string[] | undefined => {
  if (mode === 'month') return months
  if (mode === 'week') return weeks
  return undefined
}

function PrevisionsPage() {
  const { view, ready, requested } = usePrevisionsView()
  if (!ready && requested !== 'ventes') return null
  if (view === 'ventes') return <PrevisionsVentesView />
  // Production et Logistique ne lisent pas les mêmes dates : pas de données de l'une sous l'autre.
  return <ReapproProjectionView key={view} view={view} />
}

function PrevisionsVentesView() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [dailyScope, setDailyScope] = useState<BoutiqueScope | null>(null)
  const page = usePrevisionsPage()
  const { enabled, coverageQuery, coverage, range, stores, storesQuery } = page

  const cappingGuard = usePermissionGuard(['edit', 'previsions.previsions.forecast'])
  const capping = effectiveCapping(cappingGuard.authorized, search.capping)
  const setSearch = (next: Partial<typeof search>) =>
    navigate({ search: (prev) => ({ ...prev, ...next }) })

  const setFocusDate = useSetFocusDate()

  const focusDate = search.focusDate

  const mode: Granularity | 'pending' = coverageQuery.isError
    ? 'day'
    : resolveGranularity(range, coverage)
  const granularity: Granularity = mode === 'pending' ? 'day' : mode
  const chartTitle = salesChartTitle(capping, granularity)
  const effectiveRange = rangeForGranularity(range, granularity)
  const effectiveStart = effectiveRange.start
  const effectiveEnd = effectiveRange.end
  const weeks = useMemo(
    () => (mode === 'week' ? weeksOfRange({ start: effectiveStart, end: effectiveEnd }) : NO_WEEKS),
    [mode, effectiveStart, effectiveEnd],
  )
  const months = useMemo(
    () =>
      mode === 'month' ? monthsOfRange({ start: effectiveStart, end: effectiveEnd }) : NO_MONTHS,
    [mode, effectiveStart, effectiveEnd],
  )

  const snappedFocus = focusDate === undefined ? undefined : bucketStartOf(focusDate, granularity)

  useEffect(() => {
    if (mode === 'pending' || mode === 'day') return
    const rangeSettled = range.start === effectiveStart && range.end === effectiveEnd
    if (rangeSettled && snappedFocus === focusDate) return
    navigate({
      search: (prev) => ({
        ...prev,
        startDate: effectiveStart,
        endDate: effectiveEnd,
        focusDate: snappedFocus,
      }),
      replace: true,
    })
  }, [
    mode,
    range.start,
    range.end,
    effectiveStart,
    effectiveEnd,
    focusDate,
    snappedFocus,
    navigate,
  ])

  const segmentCategoriesQuery = useQuery(
    api.forecast.segmentCategories.queryOptions(
      {},
      { enabled, ...FORECAST_REFERENCE_QUERY_OPTIONS },
    ),
  )
  const scopedStoreIds = page.scope.storeIds
  const scopedProjectIds = page.scope.projectIds
  const categoriesQuery = page.productCategoriesQuery
  const categories = categoriesQuery.data ?? []

  const axisSource = SEGMENT_CATEGORY_SOURCE[search.segment]
  const axisCategoriesQuery = axisSource === 'commercial' ? categoriesQuery : segmentCategoriesQuery
  const categoriesBySource: Record<CategorySource, ForecastCategory[]> = {
    commercial: categories,
    internal: segmentCategoriesQuery.data?.internal ?? [],
    reappro: segmentCategoriesQuery.data?.reappro ?? [],
  }
  const axisCategories = categoriesBySource[axisSource]

  const setSegment = (segment: Segment) => {
    if (segment === search.segment) return
    setSearch({ segment, categoryIds: [] })
  }

  const filtersByCategory = search.categoryIds.length > 0
  const selectedCategories = axisCategories.filter((category) =>
    search.categoryIds.includes(category.id),
  )
  const productIds = selectedCategories.flatMap((category) =>
    category.products.map((product) => product.id),
  )
  const appliedCategoryIds =
    productIds.length > 0 ? selectedCategories.map((category) => category.id) : search.categoryIds

  const scopeLoading = filtersByCategory && axisCategoriesQuery.isPending
  const scopeFailed = filtersByCategory && axisCategoriesQuery.isError
  const scopeEmpty = filtersByCategory && !axisCategoriesQuery.isPending && productIds.length === 0

  const { kitchens, appliedKitchenIds } = page
  const queriesEnabled = page.scopeReady && !scopeLoading && !scopeEmpty

  const scopeQuery = {
    storeIds: scopedStoreIds,
    ...(productIds.length > 0 ? { productIds } : {}),
    ...(appliedKitchenIds.length > 0 ? { centralKitchenIds: appliedKitchenIds } : {}),
    ...(scopedProjectIds.length > 0 ? { projectIds: scopedProjectIds } : {}),
  }

  const dailyEnabled = queriesEnabled && mode === 'day'
  const weeklyEnabled = queriesEnabled && mode === 'week' && weeks.length > 0
  const monthlyEnabled = queriesEnabled && mode === 'month' && months.length > 0

  const axisGroupBy = SEGMENT_GROUP_BY[search.segment]
  const summaryRange = focusDate ? bucketRange(focusDate, granularity) : effectiveRange
  const focusBucket = snappedFocus
  const startWeek = focusBucket ?? weeks[0] ?? effectiveRange.start
  const endWeek = focusBucket ?? weeks[weeks.length - 1] ?? effectiveRange.start
  const seriesStartMonth = months[0] ?? effectiveRange.start
  const seriesEndMonth = months[months.length - 1] ?? effectiveRange.start
  const startMonth = focusBucket ?? seriesStartMonth
  const endMonth = focusBucket ?? seriesEndMonth

  const forecastQuery = useQuery(
    api.forecast.shortTermSummary.queryOptions(
      {
        query: {
          ...scopeQuery,
          startDate: summaryRange.start,
          endDate: summaryRange.end,
          capping: capping,
        },
      },
      { enabled: dailyEnabled, ...PREVISIONS_QUERY_OPTIONS },
    ),
  )

  const weeklySummaryQuery = useQuery(
    api.forecast.weeklyTableSummary.queryOptions(
      {
        query: {
          ...scopeQuery,
          startWeek,
          endWeek,
          capping: capping,
        },
      },
      { enabled: weeklyEnabled, ...PREVISIONS_QUERY_OPTIONS },
    ),
  )

  const axisReferenceQuery = useQuery(
    api.forecast.shortTermDailySeries.queryOptions(
      {
        query: {
          ...scopeQuery,
          startDate: effectiveRange.start,
          endDate: effectiveRange.end,
          groupBy: axisGroupBy,
          capping: !capping,
        },
      },
      { enabled: dailyEnabled && cappingGuard.authorized, ...PREVISIONS_QUERY_OPTIONS },
    ),
  )

  const axesQuery = useQuery(
    api.forecast.shortTermDailySeriesAxes.queryOptions(
      {
        query: {
          ...scopeQuery,
          startDate: effectiveRange.start,
          endDate: effectiveRange.end,
          groupBys: PREVISIONS_EXPORT_GROUP_BYS,
          capping: capping,
        },
      },
      { enabled: dailyEnabled, ...PREVISIONS_QUERY_OPTIONS },
    ),
  )

  const weeklyAxisReferenceQuery = useQuery(
    api.forecast.weeklyTableSeries.queryOptions(
      {
        query: {
          ...scopeQuery,
          startWeek: weeks[0] ?? effectiveRange.start,
          endWeek: weeks[weeks.length - 1] ?? effectiveRange.start,
          groupBys: [axisGroupBy],
          capping: !capping,
        },
      },
      { enabled: weeklyEnabled && cappingGuard.authorized, ...PREVISIONS_QUERY_OPTIONS },
    ),
  )

  const weeklyAxesQuery = useQuery(
    api.forecast.weeklyTableSeries.queryOptions(
      {
        query: {
          ...scopeQuery,
          startWeek: weeks[0] ?? effectiveRange.start,
          endWeek: weeks[weeks.length - 1] ?? effectiveRange.start,
          groupBys: PREVISIONS_EXPORT_GROUP_BYS,
          capping: capping,
        },
      },
      { enabled: weeklyEnabled, ...PREVISIONS_QUERY_OPTIONS },
    ),
  )

  const monthlySummaryQuery = useQuery(
    api.forecast.monthlyTableSummary.queryOptions(
      {
        query: {
          ...scopeQuery,
          startMonth,
          endMonth,
        },
      },
      { enabled: monthlyEnabled, ...PREVISIONS_QUERY_OPTIONS },
    ),
  )

  const monthlyAxesQuery = useQuery(
    api.forecast.monthlyTableSeries.queryOptions(
      {
        query: {
          ...scopeQuery,
          startMonth: seriesStartMonth,
          endMonth: seriesEndMonth,
          groupBys: PREVISIONS_EXPORT_GROUP_BYS,
        },
      },
      { enabled: monthlyEnabled, ...PREVISIONS_QUERY_OPTIONS },
    ),
  )

  const loadProductBuckets = useProductBucketsLoader(
    scopeQuery,
    effectiveRange,
    capping,
    granularity,
  )

  const isMonth = mode === 'month'
  const isWeek = mode === 'week'
  const bucketSummaryQuery = isWeek ? weeklySummaryQuery : forecastQuery
  const bucketSeriesQuery = isWeek ? weeklyAxesQuery : axesQuery
  const summaryQuery = isMonth ? monthlySummaryQuery : bucketSummaryQuery
  const seriesQuery = isMonth ? monthlyAxesQuery : bucketSeriesQuery

  const { storeLabels } = page

  const monthlySummaryData = isMonth ? monthlySummaryQuery.data : undefined
  const bucketSummaryData = isMonth ? undefined : bucketSummaryQuery.data
  const summaryData = isMonth ? monthlySummaryData : bucketSummaryData
  const forecastRows = bucketSummaryData?.products ?? []
  const summaryStores = summaryData?.stores ?? []
  const storeRows = buildStoreRows(summaryStores, storeLabels)
  const summaryProjects = summaryData?.projects ?? []
  const projectRows = buildProjectRows(summaryProjects)
  const zeroShareProducts = bucketSummaryData?.zeroShareProducts

  const axisTotals: AxisTotalsByGroupBy = useMemo(
    () => monthlySummaryData?.axisTotals ?? {},
    [monthlySummaryData],
  )

  const seriesByAxis: Partial<Record<typeof axisGroupBy, Series[]>> = useMemo(() => {
    const axes = seriesQuery.data?.seriesByAxis
    if (axes === undefined) return {}
    const convert = (entries: unknown): Series[] => {
      if (mode === 'month')
        return seriesFromMonths(entries as Parameters<typeof seriesFromMonths>[0])
      if (mode === 'week') return seriesFromWeeks(entries as Parameters<typeof seriesFromWeeks>[0])
      return seriesFromDays(entries as Parameters<typeof seriesFromDays>[0])
    }
    return Object.fromEntries(
      Object.entries(axes).map(([axis, entries]) => [axis, convert(entries)]),
    )
  }, [seriesQuery.data, mode])

  const axisReferenceSeries: Series[] | undefined = useMemo(() => {
    if (mode === 'month') return undefined
    if (mode === 'week') {
      const axes = weeklyAxisReferenceQuery.data?.seriesByAxis
      if (axes === undefined) return undefined
      return seriesFromWeeks(axes[axisGroupBy] ?? [])
    }
    if (axisReferenceQuery.data === undefined) return undefined
    return seriesFromDays(axisReferenceQuery.data.series)
  }, [mode, axisGroupBy, weeklyAxisReferenceQuery.data, axisReferenceQuery.data])

  const weekMethods = weeklyAxesQuery.data?.weeks ?? weeklySummaryQuery.data?.weeks
  const monthInfos = monthlyAxesQuery.data?.months ?? monthlySummaryQuery.data?.months
  const provenanceByBucket = useMemo<Map<string, BucketProvenance>>(
    () => (isMonth ? provenanceByMonthOf(monthInfos ?? []) : provenanceByWeekOf(weekMethods ?? [])),
    [isMonth, monthInfos, weekMethods],
  )

  const exportInputs: Omit<PrevisionsExportInputs, 'productBuckets'> = {
    search: { ...search, capping },
    range: effectiveRange,
    granularity,
    stores,
    forecastRows,
    forecastStores: summaryStores,
    projects: projectRows,
    forecastProjects: summaryProjects,
    zeroShareProducts,
    categoriesBySource,
    kitchens,
    appliedKitchenIds,
    seriesByAxis,
    axisTotals,
    provenanceByBucket: granularity === 'day' ? undefined : provenanceByBucket,
  }

  const summaryIsEmpty = isMonth
    ? summaryStores.length === 0 && summaryProjects.length === 0
    : forecastRows.length === 0
  const axisBuckets = bucketsOf(mode, weeks, months)

  const totals = isMonth
    ? buildTotalsFromAxis(summaryStores, axisTotals.uf ?? [], summaryProjects)
    : buildTotals(forecastRows)

  const treeSegment = search.segment === 'uf' ? 'commercial' : search.segment
  const categoryTree = isMonth
    ? buildCategoryTreeFromTotals(axisTotals[SEGMENT_GROUP_BY[treeSegment]] ?? [], axisCategories)
    : buildCategoryTree(forecastRows, axisCategories, treeSegment)

  const referenceDateFor = (state: DashboardCardLoadState): string | undefined =>
    state === 'transitioning' ? undefined : focusDate

  const totauxState = resolveDashboardCardState(summaryQuery)
  const boutiquesState = resolveDashboardCardState(summaryQuery, storesQuery)
  const categoriesState = resolveDashboardCardState(summaryQuery, axisCategoriesQuery)
  const chartState = resolveDashboardCardState(seriesQuery, axisCategoriesQuery)
  const exportState = resolveDashboardCardState(
    summaryQuery,
    seriesQuery,
    storesQuery,
    categoriesQuery,
    segmentCategoriesQuery,
  )

  const emptyBucketNotice = (bucket: string): string => {
    if (granularity === 'month') {
      return `Aucune production prévue en ${monthLongLabel(bucket)} sur ce périmètre.`
    }
    if (granularity === 'week') {
      return `Aucune production prévue en ${weekIsoLabel(bucket)} sur ce périmètre.`
    }
    return `Aucune production prévue le ${dayAndMonth(bucket)} sur ce périmètre.`
  }

  const content = () => {
    const { blocker } = page
    // Découpage, périmètre ou filtres encore inconnus : les quatre cartes en chargement.
    if (mode === 'pending' || blocker === 'loading' || scopeLoading) {
      return <PrevisionsLoadingGrid />
    }
    if (blocker === 'empty') return <ScopeBlockerNotice page={page} blocker={blocker} />
    if (scopeFailed) {
      return (
        <div className="flex flex-col items-start gap-2">
          <p className="text-amber-600">
            Erreur lors du chargement des catégories : le filtre de l’URL ne peut pas être appliqué.
          </p>
          <Button variant="outline" size="sm" onClick={() => axisCategoriesQuery.refetch()}>
            Réessayer
          </Button>
        </div>
      )
    }
    if (scopeEmpty) {
      return (
        <div className="flex flex-col items-start gap-2">
          <p className="text-muted-foreground">
            Les catégories filtrées ne contiennent aucun produit actif : aucune prévision ne peut
            être calculée sur ce périmètre.
          </p>
          <Button variant="outline" size="sm" onClick={() => setSearch({ categoryIds: [] })}>
            Réinitialiser les catégories
          </Button>
        </div>
      )
    }
    if (blocker !== undefined) return <ScopeBlockerNotice page={page} blocker={blocker} />
    if (summaryData !== undefined && summaryIsEmpty && !focusDate) {
      return <p className="text-muted-foreground">Aucune production prévue sur ce périmètre.</p>
    }

    const emptyFocusedDay = focusDate !== undefined && summaryData !== undefined && summaryIsEmpty

    return (
      <EmptyFocusNotice
        message={emptyFocusedDay ? emptyBucketNotice(focusDate) : undefined}
        onReset={() => setFocusDate(undefined)}
      >
        <PrevisionsGrid>
          <PrevisionsCardBoundary
            state={totauxState}
            variant="totaux"
            tone="totaux"
            errorMessage="Totaux indisponibles"
            onRetry={() => retryFailedDashboardQueries(summaryQuery)}
          >
            <TotauxCard
              totals={totals}
              storeCount={storeRows.length}
              projectCount={projectRows.length}
              referenceDate={referenceDateFor(totauxState)}
              granularity={granularity}
            />
          </PrevisionsCardBoundary>

          <PrevisionsCardBoundary
            state={chartState}
            variant="chart"
            tone="chart"
            errorMessage="Séries indisponibles"
            onRetry={() => retryFailedDashboardQueries(seriesQuery, axisCategoriesQuery)}
          >
            <ProductionChart
              title={chartTitle}
              series={seriesByAxis[axisGroupBy] ?? []}
              axisReferenceSeries={axisReferenceSeries}
              categories={axisCategories}
              measure={search.measure}
              measures={MEASURES}
              segment={search.segment}
              onMeasureChange={(measure) => setSearch({ measure })}
              granularity={granularity}
              axisBuckets={axisBuckets}
              provenanceByBucket={provenanceByBucket}
              focusedBucket={focusDate}
              onFocusBucketChange={setFocusDate}
            />
          </PrevisionsCardBoundary>

          <PrevisionsCardBoundary
            state={boutiquesState}
            variant="boutiques"
            tone="neutral"
            errorMessage="Boutiques indisponibles"
            onRetry={() => retryFailedDashboardQueries(summaryQuery, storesQuery)}
          >
            <BoutiquesCard
              stores={storeRows}
              projects={projectRows}
              columns={SALES_BOUTIQUE_COLUMNS}
              onShowDaily={setDailyScope}
              referenceDate={referenceDateFor(boutiquesState)}
              granularity={granularity}
            />
          </PrevisionsCardBoundary>

          <PrevisionsCardBoundary
            state={categoriesState}
            variant="categories"
            tone="neutral"
            errorMessage="Catégories indisponibles"
            onRetry={() => retryFailedDashboardQueries(summaryQuery, axisCategoriesQuery)}
          >
            <RotationLaunchNotesProvider from={effectiveRange.start} to={effectiveRange.end}>
              <CategoriesTable
                categories={categoryTree}
                rows={forecastRows}
                storeCount={storeRows.length}
                projectCount={projectRows.length}
                segment={search.segment}
                zeroShareProducts={zeroShareProducts}
                referenceDate={referenceDateFor(categoriesState)}
                granularity={granularity}
                ufTotals={isMonth ? (axisTotals.uf ?? []) : undefined}
              />
            </RotationLaunchNotesProvider>
          </PrevisionsCardBoundary>
        </PrevisionsGrid>
      </EmptyFocusNotice>
    )
  }

  return (
    <PrevisionsPageShell
      enabled={enabled}
      view="ventes"
      headerExtras={
        <>
          <InfoTooltip label="Prévisions" iconClassName="size-5" side="bottom">
            <div className="flex flex-col gap-1.5">
              {METHOD_LINES[granularity].map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          </InfoTooltip>
          <PrevisionsExportButton
            inputs={exportInputs}
            loadProductBuckets={loadProductBuckets}
            disabled={!queriesEnabled || exportState !== 'ready'}
          />
        </>
      }
      filters={
        <FiltersBar
          {...sharedFiltersProps(page, 'ventes')}
          range={effectiveRange}
          granularity={granularity}
          segment={search.segment}
          onSegmentChange={setSegment}
          categoryIds={appliedCategoryIds}
          onCategoryIdsChange={(categoryIds) => setSearch({ categoryIds })}
          categories={axisCategories.filter((category) => category.products.length > 0)}
          capping={capping}
          onCappingChange={(nextCapping) => setSearch({ capping: nextCapping })}
          cappingEditable={cappingGuard.authorized}
        />
      }
    >
      {content()}

      {dailyScope && (
        <BoutiqueChartModal
          scope={dailyScope}
          range={effectiveRange}
          granularity={granularity}
          productIds={productIds}
          centralKitchenIds={appliedKitchenIds}
          categories={axisCategories}
          measure={search.measure}
          segment={search.segment}
          capping={capping}
          referenceEnabled={cappingGuard.authorized}
          onClose={() => setDailyScope(null)}
        />
      )}
    </PrevisionsPageShell>
  )
}
